import { Input } from '@sjuni/ui';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { FormError } from '@/components/FormError';
import { SubmitButton } from '@/components/SubmitButton';
import { applyApiErrors } from '@/lib/forms/applyApiErrors';
import { requestPasswordReset } from '@/lib/auth/api';

interface ForgotFields {
  email: string;
}

/**
 * docs/04-API-SPEC.md §5 — `POST /admin/auth/forgot-password`.
 *
 * The server answers the same way whether or not the address exists, for the
 * reason the login screen does, so this screen shows the same confirmation
 * either way rather than reporting what the API declined to.
 */
export function ForgotPasswordPage() {
  const { t } = useTranslation();

  const [sent, setSent] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<ForgotFields>({ defaultValues: { email: '' } });

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);

    try {
      await requestPasswordReset(values.email);
      setSent(true);
    } catch (error) {
      const { formError: message, placed } = applyApiErrors(error, setError, ['email']);

      setFormError(message ?? (placed ? null : t('errors.generic')));
    }
  });

  if (sent) {
    return (
      <div className="space-y-4">
        <p role="status" className="text-body-sm text-secondary">
          {t('auth.forgot.sent')}
        </p>
        <p className="text-body-sm">
          <Link to="/login" className="text-link underline-offset-2 hover:underline">
            {t('auth.backToLogin')}
          </Link>
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate className="space-y-5">
      <FormError message={formError} />

      <Input
        {...register('email', { required: t('auth.validation.emailRequired') })}
        type="email"
        autoComplete="username"
        /* eslint-disable-next-line jsx-a11y/no-autofocus -- a screen whose only
           purpose is this one form; the rule guards against stealing focus
           mid-page, which is not what this is. */
        autoFocus
        label={t('auth.fields.email')}
        error={errors.email?.message}
        required
      />

      <SubmitButton fullWidth loading={isSubmitting}>
        {t('auth.forgot.submit')}
      </SubmitButton>

      <p className="text-center text-body-sm">
        <Link to="/login" className="text-link underline-offset-2 hover:underline">
          {t('auth.backToLogin')}
        </Link>
      </p>
    </form>
  );
}
